import { useCallback, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ContaminationDeclarationForEvent } from "../graphql/queries/contaminationDeclarationForEvent";
import { ContaminationDeclarationForEventLink } from "../graphql/queries/contaminationDeclarationForEventLink";
import { CreateContaminationDeclaration } from "../graphql/mutations/createContaminationDeclaration";
import { resolveErrorMessage } from "../utils/resolveErrorMessage";
import { useContaminationDeclarationAccess } from "./useContaminationDeclarationAccess";

export const contaminationDeclarationPath = (declarationId: string) => `/contaminationDeclarations/${declarationId}`;

/**
 * "Open or start the declaration of this ASSET_REMOVE event": looks the
 * declaration up by event, creates it when none exists and the user may, and
 * navigates to the editor. Used by the wizard follow-up, the event-history
 * status column, the event drawer and the asset's declarations section.
 */
export const useOpenContaminationDeclaration = () => {
    const client = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { canCreate } = useContaminationDeclarationAccess();
    const [createDeclaration] = useMutation(CreateContaminationDeclaration);
    const [busy, setBusy] = useState(false);

    const lookup = useCallback(async (installedBaseEventId: string): Promise<string | null> => {
        const { data } = await client.query({
            query: ContaminationDeclarationForEvent,
            variables: { installedBaseEventId },
            fetchPolicy: 'network-only',
        });
        return data?.contaminationDeclarationForEvent?.id ?? null;
    }, [client]);

    const resolveDeclarationId = useCallback(async (installedBaseEventId: string): Promise<string | null> => {
        const existing = await lookup(installedBaseEventId);
        if (existing) return existing;
        if (!canCreate) return null;
        try {
            const res = await createDeclaration({
                variables: { installedBaseEventId },
                // Keep every "for event" lookup (status chips, drawer) in sync without a refetch.
                // Through the link document, so the cached availability verdict beside it is
                // left alone — see contaminationDeclarationForEventLink.ts.
                update: (cache, { data }) => {
                    const created = data?.createContaminationDeclaration;
                    if (!created) return;
                    cache.writeQuery({
                        query: ContaminationDeclarationForEventLink,
                        variables: { installedBaseEventId },
                        data: { contaminationDeclarationForEvent: created },
                    });
                },
            });
            return res.data?.createContaminationDeclaration?.id ?? null;
        } catch (err) {
            // At most one declaration per event: if someone else started it in the
            // meantime the backend rejects the second create — open theirs instead.
            const raced = await lookup(installedBaseEventId).catch(() => null);
            if (raced) return raced;
            throw err;
        }
    }, [lookup, canCreate, createDeclaration]);

    const openForEvent = useCallback(async (
        installedBaseEventId: string,
        options?: { onBeforeNavigate?: () => void },
    ): Promise<boolean> => {
        setBusy(true);
        try {
            const declarationId = await resolveDeclarationId(installedBaseEventId);
            if (!declarationId) {
                toast.error(t('There is no declaration of contamination for this event yet, and you are not allowed to create one.'));
                return false;
            }
            options?.onBeforeNavigate?.();
            navigate(contaminationDeclarationPath(declarationId));
            return true;
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
            return false;
        } finally {
            setBusy(false);
        }
    }, [resolveDeclarationId, navigate, t]);

    return { openForEvent, resolveDeclarationId, busy, canCreate };
};
